import { ChangeDetectorRef, Directive, OnInit, inject } from '@angular/core';
import { Observable, forkJoin, of } from 'rxjs';
import { delay } from 'rxjs/operators';
import { Base } from 'src/app/shared/model/core/base.model';
import { ConsultaList } from 'src/app/shared/model/core/ConsultaList.model';
import { Aplicacion } from 'src/app/shared/model/fise/aplicacion.model';
import { Servidor } from 'src/app/shared/model/fise/servidor.model';
import { UsuarioFise } from 'src/app/shared/model/security/userbean.model';
import { FiseService } from '../fise.service';

/** Textos y límites de la plantilla compartida que cambian entre la consulta COS y la relación general. */
export interface VistaServidoresAplicaciones {
  titulo: string;
  descripcion: string;
  tituloFiltros: string;
  descripcionFiltros: string;
  descripcionServidores: string;
  usuariosSinServidor: string;
  maxLongitudSo: number;
}

/** Peticiones al backend (total y página) de cada tabla; cada pantalla apunta a sus propios servicios. */
export interface PeticionesServidoresAplicaciones {
  totalServidores(servidor: Servidor): Observable<Base>;
  paginaServidores(consulta: ConsultaList<Servidor>): Observable<ConsultaList<Servidor>>;
  totalUsuariosServidor(usuario: UsuarioFise): Observable<Base>;
  paginaUsuariosServidor(consulta: ConsultaList<UsuarioFise>): Observable<ConsultaList<UsuarioFise>>;
  totalAplicaciones(aplicacion: Aplicacion): Observable<Base>;
  paginaAplicaciones(consulta: ConsultaList<Aplicacion>): Observable<ConsultaList<Aplicacion>>;
  totalUsuariosAplicacion(usuario: UsuarioFise): Observable<Base>;
  paginaUsuariosAplicacion(consulta: ConsultaList<UsuarioFise>): Observable<ConsultaList<UsuarioFise>>;
}

/**
 * Lógica común de las pantallas de servidores y aplicaciones de FISE (consulta COS y relación general):
 * filtros del inventario, selección en cascada servidor → usuarios y aplicaciones → usuarios de la
 * aplicación, y la paginación de las cuatro tablas. Cada pantalla define su vista y sus peticiones.
 */
@Directive()
export abstract class ServidoresAplicacionesBase implements OnInit {
  abstract readonly vista: VistaServidoresAplicaciones;

  tooltipCopiarPass = 'Copiar contraseña';

  listServidor: Array<Servidor> = [];
  listAplicacion: Array<Aplicacion> = [];
  listUsuariosF: Array<UsuarioFise> = [];
  listaUsuariosxapp: Array<UsuarioFise> = [];

  servidorSelect = new Servidor();
  aplicacionSelect = new Aplicacion();

  servidorReq = new Servidor();
  aplicacionReq = new Aplicacion();

  serverTypeFilter = '';
  serverIpFilter = '';
  serverSoFilter = '';
  serverLocationFilter = '';
  usuarioServidorFilter = '';
  aplicacionFilter = '';
  usuarioAppFilter = '';

  totalRegistrosServidor = 0;
  totalRegistrosUsuarioxServidor = 0;
  totalRegistrosUsuarioxApp = 0;
  totalRegistrosA = 0;

  serverCurrentPage = 1;
  usersServerCurrentPage = 1;
  appsCurrentPage = 1;
  usersAppCurrentPage = 1;

  serverPageSize = 5;
  usersServerPageSize = 5;
  appsPageSize = 5;
  usersAppPageSize = 5;
  readonly pageSizeOptions = [5, 10, 20];

  loadingServers = false;
  loadingUsersByServer = false;
  loadingApps = false;
  loadingUsersByApp = false;

  protected readonly fiseService = inject(FiseService);
  private readonly cdr = inject(ChangeDetectorRef);

  protected abstract readonly peticiones: PeticionesServidoresAplicaciones;

  ngOnInit(): void {
    this.consultaServidores(1);
  }

  consultaServidores(page: number): void {
    this.serverCurrentPage = Math.max(1, page);
    this.loadingServers = this.listServidor.length === 0;
    this.cdr.detectChanges();

    this.servidorReq.tipoServidor = this.serverTypeFilter || undefined;
    this.servidorReq.ip = (this.serverIpFilter ?? '').trim();
    this.servidorReq.so = (this.serverSoFilter ?? '').trim();
    this.servidorReq.ubicacion = (this.serverLocationFilter ?? '').trim();

    const request = new ConsultaList<Servidor>();
    request.bean = this.servidorReq;
    request.regInicial = (this.serverCurrentPage - 1) * this.serverPageSize;
    request.regXPagina = this.serverPageSize;

    forkJoin({
      total: this.peticiones.totalServidores(this.servidorReq),
      servidores: this.peticiones.paginaServidores(request),
    }).subscribe({
      next: ({ total, servidores }) => {
        this.totalRegistrosServidor = this.resolveTotalRegistros(total, servidores, this.serverCurrentPage, this.serverPageSize);
        this.listServidor = Array.isArray(servidores?.list) ? [...servidores.list] : [];

        const maxPages = this.serverTotalPages;
        if (maxPages > 0 && this.serverCurrentPage > maxPages) {
          this.consultaServidores(maxPages);
          return;
        }

        if (maxPages === 0) {
          this.serverCurrentPage = 1;
        }

        this.loadingServers = false;
        this.cdr.detectChanges();
      },
      error: () => {
        this.listServidor = [];
        this.totalRegistrosServidor = 0;
        this.serverCurrentPage = 1;
        this.loadingServers = false;
        this.cdr.detectChanges();
      },
    });
  }

  consultaUsuariosServidor(page: number): void {
    if (!this.servidorSelect?.id_Servidor) {
      this.clearUsersByServer();
      this.cdr.detectChanges();
      return;
    }

    this.usersServerCurrentPage = Math.max(1, page);
    this.loadingUsersByServer = this.listUsuariosF.length === 0;
    this.cdr.detectChanges();

    const usuarioReq = new UsuarioFise();
    usuarioReq.id_Servidor = this.servidorSelect.id_Servidor;
    usuarioReq.usuario = (this.usuarioServidorFilter ?? '').trim();

    const request = new ConsultaList<UsuarioFise>();
    request.bean = usuarioReq;
    request.regInicial = (this.usersServerCurrentPage - 1) * this.usersServerPageSize;
    request.regXPagina = this.usersServerPageSize;

    forkJoin({
      total: this.peticiones.totalUsuariosServidor(usuarioReq),
      usuarios: this.peticiones.paginaUsuariosServidor(request),
    }).subscribe({
      next: ({ total, usuarios }) => {
        this.totalRegistrosUsuarioxServidor = this.resolveTotalRegistros(total, usuarios, this.usersServerCurrentPage, this.usersServerPageSize);
        this.listUsuariosF = Array.isArray(usuarios?.list) ? [...usuarios.list] : [];

        const maxPages = this.usersByServerTotalPages;
        if (maxPages > 0 && this.usersServerCurrentPage > maxPages) {
          this.consultaUsuariosServidor(maxPages);
          return;
        }

        if (maxPages === 0) {
          this.usersServerCurrentPage = 1;
        }

        this.loadingUsersByServer = false;
        this.cdr.detectChanges();
      },
      error: () => {
        this.clearUsersByServer();
        this.loadingUsersByServer = false;
        this.cdr.detectChanges();
      },
    });
  }

  consultaAplicaciones(page: number): void {
    if (!this.servidorSelect?.id_Servidor) {
      this.clearAplicaciones();
      this.cdr.detectChanges();
      return;
    }

    this.appsCurrentPage = Math.max(1, page);
    this.loadingApps = this.listAplicacion.length === 0;
    this.cdr.detectChanges();

    this.aplicacionReq.id_Servidor = this.servidorSelect.id_Servidor.toString();
    this.aplicacionReq.idTipo = (this.aplicacionFilter ?? '').trim();

    const request = new ConsultaList<Aplicacion>();
    request.bean = this.aplicacionReq;
    request.regInicial = (this.appsCurrentPage - 1) * this.appsPageSize;
    request.regXPagina = this.appsPageSize;

    forkJoin({
      total: this.peticiones.totalAplicaciones(this.aplicacionReq),
      aplicaciones: this.peticiones.paginaAplicaciones(request),
    }).subscribe({
      next: ({ total, aplicaciones }) => {
        this.totalRegistrosA = this.resolveTotalRegistros(total, aplicaciones, this.appsCurrentPage, this.appsPageSize);
        this.listAplicacion = Array.isArray(aplicaciones?.list) ? [...aplicaciones.list] : [];

        const maxPages = this.appsTotalPages;
        if (maxPages > 0 && this.appsCurrentPage > maxPages) {
          this.consultaAplicaciones(maxPages);
          return;
        }

        if (maxPages === 0) {
          this.appsCurrentPage = 1;
        }

        this.loadingApps = false;
        this.cdr.detectChanges();
      },
      error: () => {
        this.clearAplicaciones();
        this.loadingApps = false;
        this.cdr.detectChanges();
      },
    });
  }

  consultaUsuariosAplicacion(page: number): void {
    if (!this.aplicacionSelect?.id) {
      this.clearUsersByApp();
      this.cdr.detectChanges();
      return;
    }

    this.usersAppCurrentPage = Math.max(1, page);
    this.loadingUsersByApp = this.listaUsuariosxapp.length === 0;
    this.cdr.detectChanges();

    const usuarioReq = new UsuarioFise();
    usuarioReq.id_Aplicacion = this.aplicacionSelect.id;
    usuarioReq.usuario = (this.usuarioAppFilter ?? '').trim();

    const request = new ConsultaList<UsuarioFise>();
    request.bean = usuarioReq;
    request.regInicial = (this.usersAppCurrentPage - 1) * this.usersAppPageSize;
    request.regXPagina = this.usersAppPageSize;

    forkJoin({
      total: this.peticiones.totalUsuariosAplicacion(usuarioReq),
      usuarios: this.peticiones.paginaUsuariosAplicacion(request),
    }).subscribe({
      next: ({ total, usuarios }) => {
        this.totalRegistrosUsuarioxApp = this.resolveTotalRegistros(total, usuarios, this.usersAppCurrentPage, this.usersAppPageSize);
        this.listaUsuariosxapp = Array.isArray(usuarios?.list) ? [...usuarios.list] : [];

        const maxPages = this.usersByAppTotalPages;
        if (maxPages > 0 && this.usersAppCurrentPage > maxPages) {
          this.consultaUsuariosAplicacion(maxPages);
          return;
        }

        if (maxPages === 0) {
          this.usersAppCurrentPage = 1;
        }

        this.loadingUsersByApp = false;
        this.cdr.detectChanges();
      },
      error: () => {
        this.clearUsersByApp();
        this.loadingUsersByApp = false;
        this.cdr.detectChanges();
      },
    });
  }

  onServerTypeChange(value: string | null): void {
    this.serverTypeFilter = value || '';
    this.resetServerDependents();
    this.consultaServidores(1);
  }

  onServerIpChange(): void {
    this.resetServerDependents();
    this.consultaServidores(1);
  }

  onServerSoChange(): void {
    this.resetServerDependents();
    this.consultaServidores(1);
  }

  onServerLocationChange(): void {
    this.resetServerDependents();
    this.consultaServidores(1);
  }

  onSelectServidor(servidor: Servidor): void {
    if (this.servidorSelect?.id_Servidor === servidor?.id_Servidor) {
      return;
    }

    this.servidorSelect = servidor;
    this.usuarioServidorFilter = '';
    this.aplicacionFilter = '';
    this.clearAplicacionDependents();
    this.consultaUsuariosServidor(1);
    this.consultaAplicaciones(1);
  }

  onSelectAplicacion(aplicacion: Aplicacion): void {
    if (this.aplicacionSelect?.id === aplicacion?.id) {
      return;
    }

    this.aplicacionSelect = aplicacion;
    this.usuarioAppFilter = '';
    this.consultaUsuariosAplicacion(1);
  }

  onUsuarioServidorFilterChange(): void {
    this.consultaUsuariosServidor(1);
  }

  onAplicacionFilterChange(): void {
    this.aplicacionSelect = new Aplicacion();
    this.clearUsersByApp();
    this.consultaAplicaciones(1);
  }

  onUsuarioAppFilterChange(): void {
    this.consultaUsuariosAplicacion(1);
  }

  onServerPageSizeChange(value: number): void {
    this.serverPageSize = Number(value);
    this.consultaServidores(1);
  }

  onUsersServerPageSizeChange(value: number): void {
    this.usersServerPageSize = Number(value);
    this.consultaUsuariosServidor(1);
  }

  onAppsPageSizeChange(value: number): void {
    this.appsPageSize = Number(value);
    this.consultaAplicaciones(1);
  }

  onUsersAppPageSizeChange(value: number): void {
    this.usersAppPageSize = Number(value);
    this.consultaUsuariosAplicacion(1);
  }

  onServerPageChange(page: number): void {
    if (page === this.serverCurrentPage || page < 1 || page > this.serverTotalPages) {
      return;
    }
    this.consultaServidores(page);
  }

  onUsersServerPageChange(page: number): void {
    if (page === this.usersServerCurrentPage || page < 1 || page > this.usersByServerTotalPages) {
      return;
    }
    this.consultaUsuariosServidor(page);
  }

  onAppsPageChange(page: number): void {
    if (page === this.appsCurrentPage || page < 1 || page > this.appsTotalPages) {
      return;
    }
    this.consultaAplicaciones(page);
  }

  onUsersAppPageChange(page: number): void {
    if (page === this.usersAppCurrentPage || page < 1 || page > this.usersByAppTotalPages) {
      return;
    }
    this.consultaUsuariosAplicacion(page);
  }

  firstServerPage(): void { this.onServerPageChange(1); }
  previousServerPage(): void { this.onServerPageChange(this.serverCurrentPage - 1); }
  nextServerPage(): void { this.onServerPageChange(this.serverCurrentPage + 1); }
  lastServerPage(): void { this.onServerPageChange(this.serverTotalPages); }

  firstUsersServerPage(): void { this.onUsersServerPageChange(1); }
  previousUsersServerPage(): void { this.onUsersServerPageChange(this.usersServerCurrentPage - 1); }
  nextUsersServerPage(): void { this.onUsersServerPageChange(this.usersServerCurrentPage + 1); }
  lastUsersServerPage(): void { this.onUsersServerPageChange(this.usersByServerTotalPages); }

  firstAppsPage(): void { this.onAppsPageChange(1); }
  previousAppsPage(): void { this.onAppsPageChange(this.appsCurrentPage - 1); }
  nextAppsPage(): void { this.onAppsPageChange(this.appsCurrentPage + 1); }
  lastAppsPage(): void { this.onAppsPageChange(this.appsTotalPages); }

  firstUsersAppPage(): void { this.onUsersAppPageChange(1); }
  previousUsersAppPage(): void { this.onUsersAppPageChange(this.usersAppCurrentPage - 1); }
  nextUsersAppPage(): void { this.onUsersAppPageChange(this.usersAppCurrentPage + 1); }
  lastUsersAppPage(): void { this.onUsersAppPageChange(this.usersByAppTotalPages); }

  onclicCopiar(): void {
    this.tooltipCopiarPass = '¡Copiado!';
    of(1)
      .pipe(delay(1500))
      .subscribe({ complete: () => (this.tooltipCopiarPass = 'Copiar contraseña') });
  }

  get serverTotalPages(): number {
    return this.totalRegistrosServidor > 0 ? Math.ceil(this.totalRegistrosServidor / this.serverPageSize) : 0;
  }

  get usersByServerTotalPages(): number {
    return this.totalRegistrosUsuarioxServidor > 0 ? Math.ceil(this.totalRegistrosUsuarioxServidor / this.usersServerPageSize) : 0;
  }

  get appsTotalPages(): number {
    return this.totalRegistrosA > 0 ? Math.ceil(this.totalRegistrosA / this.appsPageSize) : 0;
  }

  get usersByAppTotalPages(): number {
    return this.totalRegistrosUsuarioxApp > 0 ? Math.ceil(this.totalRegistrosUsuarioxApp / this.usersAppPageSize) : 0;
  }

  get serverRangeEnd(): number {
    return Math.min(this.serverCurrentPage * this.serverPageSize, this.totalRegistrosServidor);
  }

  get usersServerRangeEnd(): number {
    return Math.min(this.usersServerCurrentPage * this.usersServerPageSize, this.totalRegistrosUsuarioxServidor);
  }

  get appsRangeEnd(): number {
    return Math.min(this.appsCurrentPage * this.appsPageSize, this.totalRegistrosA);
  }

  get usersAppRangeEnd(): number {
    return Math.min(this.usersAppCurrentPage * this.usersAppPageSize, this.totalRegistrosUsuarioxApp);
  }

  get showServersPagination(): boolean { return this.totalRegistrosServidor > 0; }
  get showUsersServerPagination(): boolean { return this.totalRegistrosUsuarioxServidor > 0; }
  get showAppsPagination(): boolean { return this.totalRegistrosA > 0; }
  get showUsersAppPagination(): boolean { return this.totalRegistrosUsuarioxApp > 0; }

  getServerPaginationItems(): Array<number | string> {
    return this.getPaginationItems(this.serverTotalPages, this.serverCurrentPage);
  }

  getUsersServerPaginationItems(): Array<number | string> {
    return this.getPaginationItems(this.usersByServerTotalPages, this.usersServerCurrentPage);
  }

  getAppsPaginationItems(): Array<number | string> {
    return this.getPaginationItems(this.appsTotalPages, this.appsCurrentPage);
  }

  getUsersAppPaginationItems(): Array<number | string> {
    return this.getPaginationItems(this.usersByAppTotalPages, this.usersAppCurrentPage);
  }

  isPageNumber(item: number | string): item is number {
    return typeof item === 'number';
  }

  getUsuariosServidorEmptyTitle(): string {
    return this.servidorSelect?.id_Servidor ? 'No se encontraron usuarios de servidor' : 'Seleccione un servidor';
  }

  getUsuariosServidorEmptyMessage(): string {
    return this.servidorSelect?.id_Servidor
      ? 'Ajuste el filtro para consultar otros usuarios asociados al servidor.'
      : this.vista.usuariosSinServidor;
  }

  getAplicacionesEmptyTitle(): string {
    return this.servidorSelect?.id_Servidor ? 'No se encontraron aplicaciones' : 'Seleccione un servidor';
  }

  getAplicacionesEmptyMessage(): string {
    return this.servidorSelect?.id_Servidor
      ? 'Ajuste el filtro para consultar otras aplicaciones del servidor.'
      : 'Seleccione un servidor para cargar las aplicaciones asociadas.';
  }

  getUsuariosAppEmptyTitle(): string {
    return this.aplicacionSelect?.id ? 'No se encontraron usuarios de aplicación' : 'Seleccione una aplicación';
  }

  getUsuariosAppEmptyMessage(): string {
    return this.aplicacionSelect?.id
      ? 'Ajuste el filtro para consultar otros usuarios asociados a la aplicación.'
      : 'Seleccione una aplicación para visualizar sus usuarios asociados.';
  }

  private resetServerDependents(): void {
    this.servidorSelect = new Servidor();
    this.usuarioServidorFilter = '';
    this.aplicacionFilter = '';
    this.clearUsersByServer();
    this.clearAplicacionDependents();
  }

  private clearAplicacionDependents(): void {
    this.aplicacionSelect = new Aplicacion();
    this.clearAplicaciones();
    this.clearUsersByApp();
  }

  private clearUsersByServer(): void {
    this.listUsuariosF = [];
    this.totalRegistrosUsuarioxServidor = 0;
    this.usersServerCurrentPage = 1;
    this.loadingUsersByServer = false;
  }

  private clearAplicaciones(): void {
    this.listAplicacion = [];
    this.totalRegistrosA = 0;
    this.appsCurrentPage = 1;
    this.loadingApps = false;
  }

  private clearUsersByApp(): void {
    this.listaUsuariosxapp = [];
    this.totalRegistrosUsuarioxApp = 0;
    this.usersAppCurrentPage = 1;
    this.loadingUsersByApp = false;
  }

  private getPaginationItems(totalPages: number, currentPage: number): Array<number | string> {
    if (totalPages <= 1) {
      return [1];
    }

    const pages = new Set<number>([1, totalPages, currentPage - 1, currentPage, currentPage + 1]);
    const sortedPages = Array.from(pages)
      .filter((page) => page >= 1 && page <= totalPages)
      .sort((a, b) => a - b);

    const items: Array<number | string> = [];
    for (let index = 0; index < sortedPages.length; index++) {
      const page = sortedPages[index];
      const previous = sortedPages[index - 1];
      if (previous && page - previous > 1) {
        items.push(`ellipsis-${previous}-${page}`);
      }
      items.push(page);
    }

    return items;
  }

  private resolveTotalRegistros(
    total: unknown,
    response: ConsultaList<Servidor> | ConsultaList<Aplicacion> | ConsultaList<UsuarioFise>,
    currentPage: number,
    pageSize: number
  ): number {
    const totalResponse = total as Record<string, unknown> | null;
    const responseList = response as (ConsultaList<Servidor> | ConsultaList<Aplicacion> | ConsultaList<UsuarioFise>) & Record<string, unknown>;
    const candidates = [
      totalResponse?.pTotalItems,
      totalResponse?.totalItems,
      totalResponse?.totalRegistros,
      responseList?.pTotalItems,
      responseList?.totalItems,
      responseList?.totalRegistros,
    ];

    for (const candidate of candidates) {
      const parsed = Number(candidate);
      if (Number.isFinite(parsed) && parsed > 0) {
        return parsed;
      }
    }

    const currentItems = Array.isArray(response?.list) ? response.list.length : 0;
    if (currentItems === 0) {
      return 0;
    }

    return (currentPage - 1) * pageSize + currentItems;
  }
}
